import { useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports -- this hook IS the wrapper
import { useNavigate, type NavigateOptions } from 'react-router-dom';

import type { AppUrl } from './appUrl';
import {
  buildRuntimePath,
  buildSearch,
  type NoParams,
  type PathParams,
  type QueryRecord,
} from './buildPath';
import type { AppPathTemplate } from './routePaths';

/**
 * Options accepted by `useAppNavigate`'s returned callback. Extends
 * react-router-dom's `NavigateOptions` (replace, state) with a `hash` field
 * so anchor scrolling doesn't require a fall back to raw `useNavigate`. The
 * leading `#` is added automatically if missing.
 */
export type AppNavigateOptions = NavigateOptions & { hash?: string };

/**
 * The callback `useAppNavigate` returns. `target` is a registered template
 * (`paths.X`, whose `:param` segments are then required in `params`) or an
 * `AppUrl`, a concrete URL string already validated against the registry,
 * which takes no params.
 */
export type AppNavigateFn = <P extends AppPathTemplate | AppUrl>(
  target: P,
  ...rest: NoParams<P> extends true
    ? [
        params?: PathParams<P>,
        query?: QueryRecord,
        options?: AppNavigateOptions,
      ]
    : [params: PathParams<P>, query?: QueryRecord, options?: AppNavigateOptions]
) => void;

/**
 * Type-safe in-app navigation. Prefer this over `useNavigate` +
 * `buildPath(...)` for any route declared in `routePaths.ts` — it enforces
 * required `:param` segments at compile time and reads at the call site like
 *
 *   const nav = useAppNavigate();
 *   nav(paths.NOTE, { note_id });
 *   nav(paths.SETTINGS_TAB, { main_tab, sub_tab });
 *   nav(paths.NOTE, { note_id }, undefined, { replace: true });
 *
 * A URL that only exists as a runtime string (a staffing-chat navigate event,
 * a backend redirect path) goes through `toAppUrl` / `requireAppUrl` first.
 * To rewrite only the current page's query, use `useSameRouteNavigate`.
 */
export function useAppNavigate(): AppNavigateFn {
  const navigate = useNavigate();
  return useCallback(
    (
      target: string,
      params?: Readonly<Record<string, string>>,
      query?: QueryRecord,
      options?: AppNavigateOptions
    ) => {
      const { hash, ...navOpts } = options ?? {};
      const url = buildRuntimePath(target, params, query);
      const destination = hash
        ? `${url}${hash.startsWith('#') ? hash : `#${hash}`}`
        : url;
      navigate(destination, navOpts);
    },
    [navigate]
  );
}

/** The callback `useSameRouteNavigate` returns. */
export type SameRouteNavigateFn = (
  query: QueryRecord,
  options?: AppNavigateOptions
) => void;

/**
 * Replace the current URL's query (and hash) while staying on the mounted
 * route, whatever its path:
 *
 *   const navSameRoute = useSameRouteNavigate();
 *   navSameRoute(searchParamsToQueryRecord(next), { replace: true });
 *
 * A target without a pathname resolves against the current location, so this
 * needs no registry check: the router already matched the current path. The
 * hash is dropped unless `options.hash` is given, as with `useAppNavigate`.
 */
export function useSameRouteNavigate(): SameRouteNavigateFn {
  const navigate = useNavigate();
  return useCallback(
    (query: QueryRecord, options?: AppNavigateOptions) => {
      const { hash, ...navOpts } = options ?? {};
      navigate(
        {
          search: buildSearch(query),
          hash: hash ? (hash.startsWith('#') ? hash : `#${hash}`) : '',
        },
        navOpts
      );
    },
    [navigate]
  );
}
